import { ChangeEvent, useRef, useCallback } from 'react'
import styled from '@emotion/styled'
import { MAX_FILE_SIZE } from 'utils'
import { Text, Flex } from 'components'
import { Button, EButtonType } from '@nextretreat/ui-components/dist/Button'
import { toast } from 'react-toastify'
import { rem } from 'polished'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useFormikContext } from 'formik'
import { IVenueFormValues } from 'FormPage/FormPage'
import get from 'lodash.get'
import { ReactComponent as CloseIcon } from 'assets/images/svg/close-modal.svg'
import theme from '@nextretreat/ui-components/dist/Theme'

interface IFileUploadButtonProps {
  fileObj?: File
  name: string
  /**
   * max size in bytes
   */
  maxSize?: number
  onSubmit: (file: File) => void
}

interface IDropzoneProps {
  /**
   * max size in bytes
   */
  maxSize?: number
  onDrop: (acceptedFiles: File[]) => void
}

interface IStyledDropboxWrapProps {
  canDrop?: boolean
}

interface IMultipleFileUploadProps extends IDropzoneProps {
  name: string
  onRemoveFile: (index: number) => void | undefined
}

const StyledDropboxWrap = styled.div<IStyledDropboxWrapProps>`
  border: 1px dashed ${theme.COLORS.BRAND_DEFAULT};
  cursor: pointer;
  padding: ${rem(20)} ${rem(5)};
  border-radius: ${rem(4)};
  ${({ canDrop }) => `border-style: ${canDrop ? 'solid' : 'dashed'}`}
`

const StyledFlex = styled(Flex)`
  gap: ${rem(20)};
`

const StyledCloseIcon = styled(CloseIcon)`
  width: 12px;
  height: 12px;
`

const StyledPrimaryButton = styled(Button.Primary)`
  flex-shrink: 0;
`

const CloseButton = styled(Button.Primary)`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: ${theme.COLORS.WHITE}EE !important;
  border-radius: 50px;
  width: 24px;
  height: 24px;
`

const Thumbnail = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 8px;
  object-fit: cover;
`

const StyledImage = styled('img')`
  max-width: 100%;
  max-height: 200px;
  object-fit: contain;
  border-radius: 8px;
`

export const FileUploadButton = ({
  name,
  fileObj,
  maxSize,
  onSubmit,
}: IFileUploadButtonProps) => {
  const hiddenFileInput = useRef<HTMLInputElement>(null)

  const handleFileUploadClick = () => {
    hiddenFileInput.current?.click()
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0]

    if ((!maxSize && file) || (file && file.size < (maxSize || 0))) {
      onSubmit(file)
    } else if (file) toast.error('File is too big, please upload smaller file!')
  }

  return (
    <>
      {fileObj ? (
        <Flex mb={rem(12)} alignItems="center" justifyContent="center">
          <StyledImage src={URL.createObjectURL(fileObj)} alt="main_image" />
        </Flex>
      ) : null}

      <Flex alignItems="center" justifyContent={fileObj ? 'center' : undefined}>
        <StyledPrimaryButton type="button" onClick={handleFileUploadClick}>
          <Text as="p">{fileObj ? 'Change Image' : 'Upload Image'}</Text>
        </StyledPrimaryButton>
      </Flex>

      <input
        type="file"
        accept="image/png, image/jpeg, image/webp"
        name={name}
        ref={hiddenFileInput}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </>
  )
}

const Dropzone = ({ onDrop, maxSize = MAX_FILE_SIZE }: IDropzoneProps) => {
  const dropzoneDrop = useCallback((acceptedFiles: File[]) => {
    const filteredFiles = acceptedFiles.filter(
      (file: File) => file.size < maxSize
    )

    const notUploadedFilesCount = acceptedFiles.length - filteredFiles.length
    if (notUploadedFilesCount)
      toast.error(
        "Some files weren't uploaded (reason: size or file type)! count: " +
          notUploadedFilesCount
      )
    if (filteredFiles.length) {
      onDrop(filteredFiles)
    }
  }, [])

  const onDropRejected = useCallback((rejectedFiles: FileRejection[]) => {
    toast.error(
      "Some files weren't uploaded (reason: size or file type)! count: " +
        rejectedFiles.length
    )
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/webp': ['.webp'],
    },
    onDrop: dropzoneDrop,
    onDropRejected,
    maxSize,
  })

  return (
    <StyledDropboxWrap canDrop={isDragActive} {...getRootProps()}>
      <input {...getInputProps()} />

      {isDragActive ? (
        <p>Drop the files here ...</p>
      ) : (
        <p>Drag 'n' drop some files here, or click to select files</p>
      )}
    </StyledDropboxWrap>
  )
}

export const MultipleFileUpload = ({
  name,
  onDrop,
  onRemoveFile,
}: IMultipleFileUploadProps) => {
  const { values } = useFormikContext<IVenueFormValues>()

  return (
    <>
      <Dropzone onDrop={onDrop} />

      {get(values, name)?.length ? (
        <StyledFlex mt={rem(20)} justifyContent="space-around" flexWrap="wrap">
          {get(values, name).map(({ file }: { file: File }, index: number) => (
            <Flex
              position="relative"
              key={index}
              justifyContent="space-between"
              alignItems="center"
            >
              <Thumbnail
                src={URL.createObjectURL(file)}
                alt={'file_' + index}
              />

              <CloseButton
                type="button"
                viewType={EButtonType.PLAIN}
                onClick={() => onRemoveFile(index)}
              >
                <StyledCloseIcon />
              </CloseButton>
            </Flex>
          ))}
        </StyledFlex>
      ) : null}
    </>
  )
}
